import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { getRealizadoSolicitacoes, sincronizarRealizadoPendentes, sincronizarRealizadoSolicitacao } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import { ModalOmieOs, PainelSimulacaoOmie, situacaoOmie } from "../../components/gestaoRealizado/OmieOs";
import {
  ANO_ATUAL, AvisoErro, Badge, BarraFiltros, Card, Carregando, Seletor, Vazio, botaoPequeno,
  estiloTabela, formatCompetencia, formatData, formatValor, opcoesAno, opcoesEmpresa, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Solicitações de NF no banco: as carregadas da aba Respostas (CARGA_PLANILHA) e as enviadas
// pelo formulário da plataforma (PLATAFORMA), que são copiadas para a planilha em segundo
// plano — aqui dá para ver e reenviar as que falharam. A coluna "Omie" mostra o envio da OS
// direto ao Omie (substituto do agendador): OS criada, erro, ou — antes da virada — a simulação.

const SYNC = {
  OK: { label: "Planilha OK", cor: "#10B981" },
  PENDENTE: { label: "Pendente", cor: "#F59E0B" },
  ERRO: { label: "Erro planilha", cor: "#EF4444" },
  EM_ANDAMENTO: { label: "Enviando", cor: "#F59E0B" },
  NAO_APLICAVEL: { label: "Carga CSV", cor: "#7C6FA0" },
};

export default function SolicitacoesRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const [ano, setAno] = useState(ANO_ATUAL);
  const [empresa, setEmpresa] = useState("");
  const [sync, setSync] = useState("");
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [enviando, setEnviando] = useState(null);
  const [aviso, setAviso] = useState("");
  const [omieAberta, setOmieAberta] = useState(null);
  const { dados, carregando, erro, setErro, recarregar } = useCarregar(
    () => getRealizadoSolicitacoes({ ano, empresa, sync, busca: buscaAplicada }), [ano, empresa, sync, buscaAplicada],
  );
  const T = estiloTabela(C);
  const lista = dados || [];

  const reenviar = async (acao, chave) => {
    setEnviando(chave);
    setAviso("");
    try {
      const r = await acao();
      setAviso(r.processadas !== undefined ? `${r.processadas} solicitação(ões) processada(s): ${r.OK || 0} OK, ${r.ERRO || 0} com erro.` : `Status: ${SYNC[r.status]?.label || r.status}.`);
      recarregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(null);
    }
  };

  return (
    <RealizadoLayout
      titulo="Solicitações de NF"
      isLight={isLight}
      setIsLight={setIsLight}
    >
      <BarraFiltros>
        <Seletor C={C} rotulo="Ano da solicitação" value={ano} onChange={(v) => setAno(Number(v))} opcoes={opcoesAno} />
        <Seletor C={C} rotulo="Empresa" value={empresa} onChange={setEmpresa} opcoes={opcoesEmpresa} />
        <Seletor C={C} rotulo="Planilha" value={sync} onChange={setSync} opcoes={[
          { value: "", label: "Todas" }, { value: "ERRO", label: "Com erro" }, { value: "PENDENTE", label: "Pendentes" },
          { value: "OK", label: "Sincronizadas" }, { value: "NAO_APLICAVEL", label: "Carga CSV" },
        ]} />
        <form onSubmit={(e) => { e.preventDefault(); setBuscaAplicada(busca); }} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Buscar (Enter)
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Projeto, GP ou descrição" style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: C.text, minHeight: 36, boxSizing: "border-box", width: 240 }} />
        </form>
        <button style={{ ...botaoPequeno(C.brand), marginLeft: "auto" }} disabled={!!enviando} onClick={() => reenviar(sincronizarRealizadoPendentes, "todas")}>
          <RefreshCw size={13} /> {enviando === "todas" ? "Reenviando…" : "Reenviar pendentes à planilha"}
        </button>
      </BarraFiltros>
      <PainelSimulacaoOmie C={C} onConcluido={recarregar} />
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      {aviso && <div style={{ fontSize: 12, color: C.success, fontWeight: 600 }}>{aviso}</div>}

      {carregando && !dados ? <Carregando C={C} /> : (
        <Card C={C} titulo={`${lista.length} solicitação(ões)`} subtitulo="Mostra até 500, das mais recentes para as mais antigas">
          {lista.length === 0 ? <Vazio C={C}>Nenhuma solicitação com esses filtros.</Vazio> : (
            <div style={{ ...T.wrap, maxHeight: "68vh" }}>
              <table style={{ ...T.table, minWidth: 1280 }}>
                <thead>
                  <tr>
                    <th style={T.th}>Data</th>
                    <th style={T.th}>Origem</th>
                    <th style={T.th}>Projeto</th>
                    <th style={T.th}>Empresa</th>
                    <th style={T.th}>GP</th>
                    <th style={T.th}>Competência(s)</th>
                    <th style={{ ...T.th, ...T.thNum }}>Valor</th>
                    <th style={T.th}>NF</th>
                    <th style={T.th}>Planilha</th>
                    <th style={T.th} title="OS criada pela plataforma no Omie (ou a simulação, antes da virada)">Omie</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((s) => {
                    const st = SYNC[s.syncStatus] || { label: s.syncStatus, cor: C.muted };
                    return (
                      <tr key={s.id}>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>{formatData(s.dataSolicitacao)}</td>
                        <td style={T.td}>{s.origem === "PLATAFORMA" ? "Formulário" : `Planilha l.${s.linhaPlanilha}`}</td>
                        <td style={{ ...T.td, fontStyle: s.projetoId ? "normal" : "italic", maxWidth: 220 }} title={s.descricao}>{s.projeto || "(sem projeto)"}</td>
                        <td style={T.td}>{s.empresa || "—"}</td>
                        <td style={T.td}>{s.gp || "—"}</td>
                        <td style={T.td}>{s.competencias.map((c) => formatCompetencia(c.competencia)).join(", ")}</td>
                        <td style={{ ...T.td, ...T.tdNum }}>{formatValor(s.valor)}</td>
                        <td style={T.td}>{s.nfs.join(", ") || "—"}</td>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>
                          <Badge cor={st.cor} title={s.syncErro || undefined}>{st.label}</Badge>
                          {s.origem === "PLATAFORMA" && s.syncStatus !== "OK" && (
                            <button
                              onClick={() => reenviar(() => sincronizarRealizadoSolicitacao(s.id), s.id)}
                              disabled={!!enviando}
                              style={{ marginLeft: 6, background: "none", border: "none", color: C.brand, cursor: "pointer", fontSize: 11, fontWeight: 700, padding: 0 }}
                            >
                              {enviando === s.id ? "enviando…" : "reenviar"}
                            </button>
                          )}
                        </td>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>
                          {(() => {
                            const o = situacaoOmie(s, C);
                            return (
                              <button onClick={() => setOmieAberta(s.id)} title={o.dica || "Ver simulação"} style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                                <Badge cor={o.cor}>{o.rotulo}</Badge>
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      {omieAberta && <ModalOmieOs C={C} solicitacaoId={omieAberta} onClose={() => setOmieAberta(null)} onAtualizado={recarregar} />}
    </RealizadoLayout>
  );
}
